import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { 
  Car, Calendar, MessageSquare, Image, CreditCard, Settings,
  LogOut, Plus, Trash2, Edit, Eye, EyeOff, X, Check, Save, Star,
  Home, Info, Wrench, Phone, FileText, Camera, Menu, Package
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import ImageUploader from "@/components/ImageUploader";
import MultiImageUploader from "@/components/MultiImageUploader";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("general");
  const [cars, setCars] = useState([]);
  const [products, setProducts] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [payments, setPayments] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingSettings, setSavingSettings] = useState(false);
  const [showCarDialog, setShowCarDialog] = useState(false);
  const [showProductDialog, setShowProductDialog] = useState(false);
  const [showGalleryDialog, setShowGalleryDialog] = useState(false);
  const [showReviewDialog, setShowReviewDialog] = useState(false);
  const [editingCar, setEditingCar] = useState(null);
  const [editingProduct, setEditingProduct] = useState(null);
  const [newCar, setNewCar] = useState({
    marque: "", modele: "", annee: 2023, kilometrage: 0, prix: 0,
    carburant: "Essence", transmission: "Automatique", puissance: "",
    couleur: "", description: "", images: [], disponible: true
  });
  const [newProduct, setNewProduct] = useState({
    nom: "", categorie: "calculateur-moteur", description: "", prix: 0,
    prix_promo: null, images: [], en_stock: true, reference: ""
  });
  const [newGalleryImage, setNewGalleryImage] = useState({ url: "", titre: "", description: "" });
  const [newReview, setNewReview] = useState({ nom: "", note: 5, service: "", commentaire: "" });

  const token = localStorage.getItem("admin_token");
  const getAuthHeaders = () => ({ headers: { Authorization: `Bearer ${token}` } });

  useEffect(() => {
    if (!token) {
      navigate("/admin");
      return;
    }
    fetchData();
  }, [token, navigate]);

  const fetchData = async () => {
    try {
      const [carsRes, productsRes, apptRes, contactsRes, paymentsRes, galleryRes, settingsRes, reviewsRes] = await Promise.all([
        axios.get(`${API}/cars`),
        axios.get(`${API}/products`),
        axios.get(`${API}/appointments`, getAuthHeaders()),
        axios.get(`${API}/contacts`, getAuthHeaders()),
        axios.get(`${API}/payments`, getAuthHeaders()),
        axios.get(`${API}/gallery`),
        axios.get(`${API}/settings`),
        axios.get(`${API}/reviews/all`, getAuthHeaders())
      ]);
      setCars(carsRes.data);
      setProducts(productsRes.data);
      setAppointments(apptRes.data);
      setContacts(contactsRes.data);
      setPayments(paymentsRes.data);
      setGallery(galleryRes.data);
      setSettings(settingsRes.data);
      setReviews(reviewsRes.data);
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.removeItem("admin_token");
        navigate("/admin");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("admin_token");
    navigate("/admin");
  };

  const updateSetting = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const handleSaveSettings = async () => {
    setSavingSettings(true);
    try {
      await axios.put(`${API}/settings`, settings, getAuthHeaders());
      toast.success("Modifications enregistrées !");
    } catch (error) {
      toast.error("Erreur lors de l'enregistrement");
    } finally {
      setSavingSettings(false);
    }
  };

  // Car handlers
  const handleSaveCar = async () => {
    try {
      if (editingCar) {
        await axios.put(`${API}/cars/${editingCar.id}`, newCar, getAuthHeaders());
        toast.success("Véhicule modifié");
      } else {
        await axios.post(`${API}/cars`, newCar, getAuthHeaders());
        toast.success("Véhicule ajouté");
      }
      setShowCarDialog(false);
      setEditingCar(null);
      setNewCar({ marque: "", modele: "", annee: 2023, kilometrage: 0, prix: 0, carburant: "Essence", transmission: "Automatique", puissance: "", couleur: "", description: "", images: [], disponible: true });
      fetchData();
    } catch (error) {
      toast.error("Erreur");
    }
  };

  const handleDeleteCar = async (id) => {
    if (!window.confirm("Supprimer ce véhicule ?")) return;
    try {
      await axios.delete(`${API}/cars/${id}`, getAuthHeaders());
      toast.success("Véhicule supprimé");
      fetchData();
    } catch (error) {
      toast.error("Erreur");
    }
  };

  const handleEditCar = (car) => {
    setEditingCar(car);
    setNewCar(car);
    setShowCarDialog(true);
  };

  // Gallery handlers
  const handleAddGalleryImage = async () => {
    if (!newGalleryImage.url) { toast.error("URL requise"); return; }
    try {
      await axios.post(`${API}/gallery`, newGalleryImage, getAuthHeaders());
      toast.success("Image ajoutée");
      setShowGalleryDialog(false);
      setNewGalleryImage({ url: "", titre: "", description: "" });
      fetchData();
    } catch (error) {
      toast.error("Erreur");
    }
  };

  const handleDeleteGalleryImage = async (id) => {
    if (!window.confirm("Supprimer cette image ?")) return;
    try {
      await axios.delete(`${API}/gallery/${id}`, getAuthHeaders());
      toast.success("Image supprimée");
      fetchData();
    } catch (error) {
      toast.error("Erreur");
    }
  };

  // Product handlers
  const handleSaveProduct = async () => {
    try {
      const productData = { ...newProduct };
      if (!productData.prix_promo) delete productData.prix_promo;
      
      if (editingProduct) {
        await axios.put(`${API}/products/${editingProduct.id}`, productData, getAuthHeaders());
        toast.success("Produit modifié");
      } else {
        await axios.post(`${API}/products`, productData, getAuthHeaders());
        toast.success("Produit ajouté");
      }
      setShowProductDialog(false);
      setEditingProduct(null);
      setNewProduct({ nom: "", categorie: "calculateur-moteur", description: "", prix: 0, prix_promo: null, images: [], en_stock: true, reference: "" });
      fetchData();
    } catch (error) {
      toast.error("Erreur");
    }
  };

  const handleDeleteProduct = async (id) => {
    if (!window.confirm("Supprimer ce produit ?")) return;
    try {
      await axios.delete(`${API}/products/${id}`, getAuthHeaders());
      toast.success("Produit supprimé");
      fetchData();
    } catch (error) {
      toast.error("Erreur");
    }
  };

  const handleEditProduct = (product) => {
    setEditingProduct(product);
    setNewProduct(product);
    setShowProductDialog(true);
  };

  // Review handlers
  const handleApproveReview = async (id) => {
    try {
      await axios.put(`${API}/reviews/${id}/approve`, {}, getAuthHeaders());
      toast.success("Avis approuvé");
      fetchData();
    } catch (error) {
      toast.error("Erreur");
    }
  };

  const handleDeleteReview = async (id) => {
    if (!window.confirm("Supprimer cet avis ?")) return;
    try {
      await axios.delete(`${API}/reviews/${id}`, getAuthHeaders());
      toast.success("Avis supprimé");
      fetchData();
    } catch (error) {
      toast.error("Erreur");
    }
  };

  const handleAddReview = async () => {
    if (!newReview.nom || !newReview.commentaire) {
      toast.error("Remplissez le nom et le commentaire");
      return;
    }
    try {
      await axios.post(`${API}/reviews/admin`, newReview, getAuthHeaders());
      toast.success("Avis ajouté et publié");
      setShowReviewDialog(false);
      setNewReview({ nom: "", note: 5, service: "", commentaire: "" });
      fetchData();
    } catch (error) {
      toast.error("Erreur");
    }
  };

  const renderStars = (note) => [...Array(5)].map((_, i) => (
    <Star key={i} className={`w-4 h-4 ${i < note ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`} />
  ));

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500" />
      </div>
    );
  }

  const menuItems = [
    { id: "general", label: "Infos Générales", icon: Settings },
    { id: "accueil", label: "Page Accueil", icon: Home },
    { id: "services", label: "Page Services", icon: Wrench },
    { id: "apropos", label: "Page À Propos", icon: Info },
    { id: "autres", label: "Autres Pages", icon: FileText },
    { id: "footer", label: "Footer", icon: Menu },
    { id: "voitures", label: "Véhicules", icon: Car },
    { id: "produits", label: "Produits", icon: Package },
    { id: "galerie", label: "Galerie", icon: Camera },
    { id: "avis", label: "Avis Clients", icon: Star },
    { id: "messages", label: "Messages", icon: MessageSquare },
    { id: "paiements", label: "Paiements", icon: CreditCard },
  ];

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-dashboard">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center">
          <h1 className="text-2xl font-bold text-gray-900">Administration <span className="text-yellow-500">ACICAR</span></h1>
          <Button variant="outline" onClick={handleLogout} className="text-gray-600">
            <LogOut className="w-4 h-4 mr-2" /> Déconnexion
          </Button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex gap-8">
          {/* Sidebar Menu */}
          <div className="w-64 flex-shrink-0">
            <div className="bg-white rounded-xl border border-gray-200 p-4 sticky top-24">
              <nav className="space-y-1">
                {menuItems.map(item => (
                  <button
                    key={item.id}
                    onClick={() => setActiveTab(item.id)}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors ${
                      activeTab === item.id 
                        ? 'bg-yellow-50 text-yellow-700 font-medium' 
                        : 'text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    <item.icon className="w-5 h-5" />
                    {item.label}
                  </button>
                ))}
              </nav>
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1 min-w-0">
            {/* Infos Générales */}
            {activeTab === "general" && settings && (
              <div className="bg-white rounded-xl border border-gray-200 p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Informations Générales</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label>Nom du garage</Label>
                    <Input value={settings.nom_garage || ""} onChange={(e) => updateSetting("nom_garage", e.target.value)} className="mt-2" />
                  </div>
                  <div>
                    <Label>Téléphone</Label>
                    <Input value={settings.telephone || ""} onChange={(e) => updateSetting("telephone", e.target.value)} className="mt-2" />
                  </div>
                  <div>
                    <Label>Email</Label>
                    <Input value={settings.email || ""} onChange={(e) => updateSetting("email", e.target.value)} className="mt-2" />
                  </div>
                  <div>
                    <Label>Adresse</Label>
                    <Input value={settings.adresse || ""} onChange={(e) => updateSetting("adresse", e.target.value)} className="mt-2" />
                  </div>
                  <div>
                    <Label>Code postal</Label>
                    <Input value={settings.code_postal || ""} onChange={(e) => updateSetting("code_postal", e.target.value)} className="mt-2" />
                  </div>
                  <div>
                    <Label>Ville</Label>
                    <Input value={settings.ville || ""} onChange={(e) => updateSetting("ville", e.target.value)} className="mt-2" />
                  </div>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mt-8 mb-4">Horaires d'ouverture</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <Label>Semaine</Label>
                    <Input value={settings.horaires_semaine || ""} onChange={(e) => updateSetting("horaires_semaine", e.target.value)} className="mt-2" placeholder="Lun - Ven: 8h30 - 18h30" />
                  </div>
                  <div>
                    <Label>Samedi</Label>
                    <Input value={settings.horaires_samedi || ""} onChange={(e) => updateSetting("horaires_samedi", e.target.value)} className="mt-2" placeholder="Samedi: 9h00 - 17h00" />
                  </div>
                  <div>
                    <Label>Dimanche</Label>
                    <Input value={settings.horaires_dimanche || ""} onChange={(e) => updateSetting("horaires_dimanche", e.target.value)} className="mt-2" placeholder="Dimanche: Fermé" />
                  </div>
                </div>
                <div className="mt-8 flex justify-end">
                  <Button onClick={handleSaveSettings} disabled={savingSettings} className="bg-yellow-500 hover:bg-yellow-600 text-white">
                    <Save className="w-4 h-4 mr-2" /> {savingSettings ? "Enregistrement..." : "Enregistrer"}
                  </Button>
                </div>
              </div>
            )}

            {/* Page Accueil */}
            {activeTab === "accueil" && settings && (
              <div className="space-y-6">
                <div className="bg-white rounded-xl border border-gray-200 p-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">Section Hero (Bannière principale)</h2>
                  <div className="space-y-6">
                    <div>
                      <Label>Titre principal</Label>
                      <Input value={settings.hero_titre || ""} onChange={(e) => updateSetting("hero_titre", e.target.value)} className="mt-2" />
                    </div>
                    <div>
                      <Label>Sous-titre</Label>
                      <Textarea value={settings.hero_sous_titre || ""} onChange={(e) => updateSetting("hero_sous_titre", e.target.value)} className="mt-2" />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Texte bouton 1</Label>
                        <Input value={settings.hero_btn1_text || ""} onChange={(e) => updateSetting("hero_btn1_text", e.target.value)} className="mt-2" />
                      </div>
                      <div>
                        <Label>Texte bouton 2</Label>
                        <Input value={settings.hero_btn2_text || ""} onChange={(e) => updateSetting("hero_btn2_text", e.target.value)} className="mt-2" />
                      </div>
                    </div>
                    <ImageUploader label="Image de fond" value={settings.hero_image || ""} onChange={(url) => updateSetting("hero_image", url)} />
                  </div>
                </div>
                <div className="bg-white rounded-xl border border-gray-200 p-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">Sections de la page</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label>Section Services - Titre</Label>
                      <Input value={settings.home_services_titre || ""} onChange={(e) => updateSetting("home_services_titre", e.target.value)} className="mt-2" />
                    </div>
                    <div>
                      <Label>Section Services - Description</Label>
                      <Input value={settings.home_services_description || ""} onChange={(e) => updateSetting("home_services_description", e.target.value)} className="mt-2" />
                    </div>
                    <div>
                      <Label>Section Véhicules - Titre</Label>
                      <Input value={settings.home_cars_titre || ""} onChange={(e) => updateSetting("home_cars_titre", e.target.value)} className="mt-2" />
                    </div>
                    <div>
                      <Label>Section Véhicules - Description</Label>
                      <Input value={settings.home_cars_description || ""} onChange={(e) => updateSetting("home_cars_description", e.target.value)} className="mt-2" />
                    </div>
                    <div>
                      <Label>Section CTA - Titre</Label>
                      <Input value={settings.home_cta_titre || ""} onChange={(e) => updateSetting("home_cta_titre", e.target.value)} className="mt-2" />
                    </div>
                    <div>
                      <Label>Section CTA - Description</Label>
                      <Input value={settings.home_cta_description || ""} onChange={(e) => updateSetting("home_cta_description", e.target.value)} className="mt-2" />
                    </div>
                  </div>
                </div>
                <div className="flex justify-end">
                  <Button onClick={handleSaveSettings} disabled={savingSettings} className="bg-yellow-500 hover:bg-yellow-600 text-white">
                    <Save className="w-4 h-4 mr-2" /> {savingSettings ? "Enregistrement..." : "Enregistrer"}
                  </Button>
                </div>
              </div>
            )}

            {/* Page Services */}
            {activeTab === "services" && settings && (
              <div className="space-y-6">
                <div className="bg-white rounded-xl border border-gray-200 p-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">Page Services - Général</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label>Titre de la page</Label>
                      <Input value={settings.services_titre || ""} onChange={(e) => updateSetting("services_titre", e.target.value)} className="mt-2" />
                    </div>
                    <div>
                      <Label>Description</Label>
                      <Input value={settings.services_description || ""} onChange={(e) => updateSetting("services_description", e.target.value)} className="mt-2" />
                    </div>
                  </div>
                </div>

                {/* Service 1 */}
                <div className="bg-white rounded-xl border border-gray-200 p-8">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Service 1 - Mécanique</h3>
                  <div className="space-y-4">
                    <div><Label>Titre</Label><Input value={settings.service1_titre || ""} onChange={(e) => updateSetting("service1_titre", e.target.value)} className="mt-2" /></div>
                    <div><Label>Description</Label><Textarea value={settings.service1_description || ""} onChange={(e) => updateSetting("service1_description", e.target.value)} className="mt-2" /></div>
                    <div><Label>Points clés (séparés par |)</Label><Input value={settings.service1_features || ""} onChange={(e) => updateSetting("service1_features", e.target.value)} className="mt-2" placeholder="Point 1|Point 2|Point 3" /></div>
                    <MultiImageUploader label="Photos du service (jusqu'à 20)" values={(settings.service1_images || "").split(",").filter(s => s)} onChange={(urls) => updateSetting("service1_images", urls.join(","))} maxImages={20} />
                  </div>
                </div>

                {/* Service 2 */}
                <div className="bg-white rounded-xl border border-gray-200 p-8">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Service 2 - Vente de Véhicules</h3>
                  <div className="space-y-4">
                    <div><Label>Titre</Label><Input value={settings.service2_titre || ""} onChange={(e) => updateSetting("service2_titre", e.target.value)} className="mt-2" /></div>
                    <div><Label>Description</Label><Textarea value={settings.service2_description || ""} onChange={(e) => updateSetting("service2_description", e.target.value)} className="mt-2" /></div>
                    <div><Label>Points clés (séparés par |)</Label><Input value={settings.service2_features || ""} onChange={(e) => updateSetting("service2_features", e.target.value)} className="mt-2" /></div>
                    <MultiImageUploader label="Photos du service (jusqu'à 20)" values={(settings.service2_images || "").split(",").filter(s => s)} onChange={(urls) => updateSetting("service2_images", urls.join(","))} maxImages={20} />
                  </div>
                </div>

                {/* Service 3 */}
                <div className="bg-white rounded-xl border border-gray-200 p-8">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Service 3 - Contrôle Technique</h3>
                  <div className="space-y-4">
                    <div><Label>Titre</Label><Input value={settings.service3_titre || ""} onChange={(e) => updateSetting("service3_titre", e.target.value)} className="mt-2" /></div>
                    <div><Label>Description</Label><Textarea value={settings.service3_description || ""} onChange={(e) => updateSetting("service3_description", e.target.value)} className="mt-2" /></div>
                    <div><Label>Points clés (séparés par |)</Label><Input value={settings.service3_features || ""} onChange={(e) => updateSetting("service3_features", e.target.value)} className="mt-2" /></div>
                    <MultiImageUploader label="Photos du service (jusqu'à 20)" values={(settings.service3_images || "").split(",").filter(s => s)} onChange={(urls) => updateSetting("service3_images", urls.join(","))} maxImages={20} />
                  </div>
                </div>

                {/* Service 4 */}
                <div className="bg-white rounded-xl border border-gray-200 p-8">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Service 4 - Serrurerie Automobile</h3>
                  <div className="space-y-4">
                    <div><Label>Titre</Label><Input value={settings.service4_titre || ""} onChange={(e) => updateSetting("service4_titre", e.target.value)} className="mt-2" /></div>
                    <div><Label>Description</Label><Textarea value={settings.service4_description || ""} onChange={(e) => updateSetting("service4_description", e.target.value)} className="mt-2" /></div>
                    <div><Label>Points clés (séparés par |)</Label><Input value={settings.service4_features || ""} onChange={(e) => updateSetting("service4_features", e.target.value)} className="mt-2" /></div>
                    <MultiImageUploader label="Photos du service (jusqu'à 20)" values={(settings.service4_images || "").split(",").filter(s => s)} onChange={(urls) => updateSetting("service4_images", urls.join(","))} maxImages={20} />
                  </div>
                </div>

                {/* Service 5 */}
                <div className="bg-white rounded-xl border border-gray-200 p-8">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Service 5 - Électronique Auto</h3>
                  <div className="space-y-4">
                    <div><Label>Titre</Label><Input value={settings.service5_titre || ""} onChange={(e) => updateSetting("service5_titre", e.target.value)} className="mt-2" /></div>
                    <div><Label>Description</Label><Textarea value={settings.service5_description || ""} onChange={(e) => updateSetting("service5_description", e.target.value)} className="mt-2" /></div>
                    <div><Label>Points clés (séparés par |)</Label><Input value={settings.service5_features || ""} onChange={(e) => updateSetting("service5_features", e.target.value)} className="mt-2" /></div>
                    <MultiImageUploader label="Photos du service (jusqu'à 20)" values={(settings.service5_images || "").split(",").filter(s => s)} onChange={(urls) => updateSetting("service5_images", urls.join(","))} maxImages={20} />
                  </div>
                </div>

                {/* Service 6 */}
                <div className="bg-white rounded-xl border border-gray-200 p-8">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Service 6 - Carrosserie</h3>
                  <div className="space-y-4">
                    <div><Label>Titre</Label><Input value={settings.service6_titre || ""} onChange={(e) => updateSetting("service6_titre", e.target.value)} className="mt-2" /></div>
                    <div><Label>Description</Label><Textarea value={settings.service6_description || ""} onChange={(e) => updateSetting("service6_description", e.target.value)} className="mt-2" /></div>
                    <div><Label>Points clés (séparés par |)</Label><Input value={settings.service6_features || ""} onChange={(e) => updateSetting("service6_features", e.target.value)} className="mt-2" /></div>
                    <MultiImageUploader label="Photos du service (jusqu'à 20)" values={(settings.service6_images || "").split(",").filter(s => s)} onChange={(urls) => updateSetting("service6_images", urls.join(","))} maxImages={20} />
                  </div>
                </div>

                <div className="flex justify-end">
                  <Button onClick={handleSaveSettings} disabled={savingSettings} className="bg-yellow-500 hover:bg-yellow-600 text-white">
                    <Save className="w-4 h-4 mr-2" /> {savingSettings ? "Enregistrement..." : "Enregistrer"}
                  </Button>
                </div>
              </div>
            )}

            {/* Page À Propos */}
            {activeTab === "apropos" && settings && (
              <div className="space-y-6">
                <div className="bg-white rounded-xl border border-gray-200 p-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">Page À Propos</h2>
                  <div className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <Label>Titre de la page</Label>
                        <Input value={settings.about_titre || ""} onChange={(e) => updateSetting("about_titre", e.target.value)} className="mt-2" />
                      </div>
                      <div>
                        <Label>Titre section histoire</Label>
                        <Input value={settings.about_histoire_titre || ""} onChange={(e) => updateSetting("about_histoire_titre", e.target.value)} className="mt-2" />
                      </div>
                    </div>
                    <div>
                      <Label>Description courte</Label>
                      <Textarea value={settings.about_description || ""} onChange={(e) => updateSetting("about_description", e.target.value)} className="mt-2" />
                    </div>
                    <div>
                      <Label>Notre histoire</Label>
                      <Textarea value={settings.about_histoire || ""} onChange={(e) => updateSetting("about_histoire", e.target.value)} className="mt-2 min-h-[100px]" />
                    </div>
                    <div>
                      <Label>Notre mission</Label>
                      <Textarea value={settings.about_mission || ""} onChange={(e) => updateSetting("about_mission", e.target.value)} className="mt-2 min-h-[100px]" />
                    </div>
                    <div className="grid grid-cols-3 gap-6">
                      <div>
                        <Label>Années d'expérience</Label>
                        <Input value={settings.about_stats_annees || ""} onChange={(e) => updateSetting("about_stats_annees", e.target.value)} className="mt-2" placeholder="15+" />
                      </div>
                      <div>
                        <Label>Clients satisfaits</Label>
                        <Input value={settings.about_stats_clients || ""} onChange={(e) => updateSetting("about_stats_clients", e.target.value)} className="mt-2" placeholder="5000+" />
                      </div>
                      <div>
                        <Label>Véhicules vendus</Label>
                        <Input value={settings.about_stats_vehicules || ""} onChange={(e) => updateSetting("about_stats_vehicules", e.target.value)} className="mt-2" placeholder="500+" />
                      </div>
                    </div>
                    <ImageUploader label="Image de la page" value={settings.about_image || ""} onChange={(url) => updateSetting("about_image", url)} />
                  </div>
                </div>
                <div className="flex justify-end">
                  <Button onClick={handleSaveSettings} disabled={savingSettings} className="bg-yellow-500 hover:bg-yellow-600 text-white">
                    <Save className="w-4 h-4 mr-2" /> {savingSettings ? "Enregistrement..." : "Enregistrer"}
                  </Button>
                </div>
              </div>
            )}

            {/* Autres Pages */}
            {activeTab === "autres" && settings && (
              <div className="space-y-6">
                <div className="bg-white rounded-xl border border-gray-200 p-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">Page Voitures</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div><Label>Titre</Label><Input value={settings.voitures_titre || ""} onChange={(e) => updateSetting("voitures_titre", e.target.value)} className="mt-2" /></div>
                    <div><Label>Description</Label><Input value={settings.voitures_description || ""} onChange={(e) => updateSetting("voitures_description", e.target.value)} className="mt-2" /></div>
                  </div>
                </div>
                <div className="bg-white rounded-xl border border-gray-200 p-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">Page Contact</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div><Label>Titre</Label><Input value={settings.contact_titre || ""} onChange={(e) => updateSetting("contact_titre", e.target.value)} className="mt-2" /></div>
                    <div><Label>Description</Label><Input value={settings.contact_description || ""} onChange={(e) => updateSetting("contact_description", e.target.value)} className="mt-2" /></div>
                  </div>
                </div>
                <div className="bg-white rounded-xl border border-gray-200 p-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">Page Galerie</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div><Label>Titre</Label><Input value={settings.galerie_titre || ""} onChange={(e) => updateSetting("galerie_titre", e.target.value)} className="mt-2" /></div>
                    <div><Label>Description</Label><Input value={settings.galerie_description || ""} onChange={(e) => updateSetting("galerie_description", e.target.value)} className="mt-2" /></div>
                  </div>
                </div>
                <div className="bg-white rounded-xl border border-gray-200 p-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">Page Avis</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div><Label>Titre</Label><Input value={settings.avis_titre || ""} onChange={(e) => updateSetting("avis_titre", e.target.value)} className="mt-2" /></div>
                    <div><Label>Description</Label><Input value={settings.avis_description || ""} onChange={(e) => updateSetting("avis_description", e.target.value)} className="mt-2" /></div>
                  </div>
                  <div className="mt-6">
                    <Label>Lien vers vos avis Google</Label>
                    <Input 
                      value={settings.google_reviews_url || ""} 
                      onChange={(e) => updateSetting("google_reviews_url", e.target.value)} 
                      className="mt-2" 
                      placeholder="https://g.page/r/votre-etablissement/review"
                    />
                    <p className="text-xs text-gray-400 mt-1">
                      Collez le lien vers la page des avis Google de votre établissement. Un bouton "Lire tous les avis Google" apparaîtra sur la page Avis.
                    </p>
                  </div>
                </div>
                <div className="flex justify-end">
                  <Button onClick={handleSaveSettings} disabled={savingSettings} className="bg-yellow-500 hover:bg-yellow-600 text-white">
                    <Save className="w-4 h-4 mr-2" /> {savingSettings ? "Enregistrement..." : "Enregistrer"}
                  </Button>
                </div>
              </div>
            )}

            {/* Footer */}
            {activeTab === "footer" && settings && (
              <div className="bg-white rounded-xl border border-gray-200 p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Footer</h2>
                <div className="space-y-6">
                  <div>
                    <Label>Description</Label>
                    <Textarea value={settings.footer_description || ""} onChange={(e) => updateSetting("footer_description", e.target.value)} className="mt-2" />
                  </div>
                  <div>
                    <Label>Copyright</Label>
                    <Input value={settings.footer_copyright || ""} onChange={(e) => updateSetting("footer_copyright", e.target.value)} className="mt-2" />
                  </div>
                </div>
                <div className="mt-8 flex justify-end">
                  <Button onClick={handleSaveSettings} disabled={savingSettings} className="bg-yellow-500 hover:bg-yellow-600 text-white">
                    <Save className="w-4 h-4 mr-2" /> {savingSettings ? "Enregistrement..." : "Enregistrer"}
                  </Button>
                </div>
              </div>
            )}

            {/* Véhicules */}
            {activeTab === "voitures" && (
              <div className="bg-white rounded-xl border border-gray-200 p-8">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold text-gray-900">Véhicules ({cars.length})</h2>
                  <Button onClick={() => { setEditingCar(null); setNewCar({ marque: "", modele: "", annee: 2023, kilometrage: 0, prix: 0, carburant: "Essence", transmission: "Automatique", puissance: "", couleur: "", description: "", images: [], disponible: true }); setShowCarDialog(true); }} className="bg-yellow-500 hover:bg-yellow-600 text-white">
                    <Plus className="w-4 h-4 mr-2" /> Ajouter
                  </Button>
                </div>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Photo</TableHead>
                        <TableHead>Véhicule</TableHead>
                        <TableHead>Prix</TableHead>
                        <TableHead>Statut</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {cars.map(car => (
                        <TableRow key={car.id}>
                          <TableCell>
                            <img src={car.images?.[0]?.startsWith("http") ? car.images[0] : `${process.env.REACT_APP_BACKEND_URL}${car.images?.[0]}`} alt="" className="w-16 h-12 object-cover rounded" />
                          </TableCell>
                          <TableCell className="font-medium">{car.marque} {car.modele} ({car.annee})</TableCell>
                          <TableCell>{car.prix?.toLocaleString()} €</TableCell>
                          <TableCell>
                            <span className={`px-2 py-1 rounded text-xs ${car.disponible ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                              {car.disponible ? "Disponible" : "Vendu"}
                            </span>
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-2">
                              <Button size="sm" variant="outline" onClick={() => handleEditCar(car)}><Edit className="w-4 h-4" /></Button>
                              <Button size="sm" variant="destructive" onClick={() => handleDeleteCar(car.id)}><Trash2 className="w-4 h-4" /></Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}

            {/* Galerie */}
            {activeTab === "galerie" && (
              <div className="bg-white rounded-xl border border-gray-200 p-8">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold text-gray-900">Galerie ({gallery.length} images)</h2>
                  <Button onClick={() => setShowGalleryDialog(true)} className="bg-yellow-500 hover:bg-yellow-600 text-white">
                    <Plus className="w-4 h-4 mr-2" /> Ajouter
                  </Button>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {gallery.map(img => (
                    <div key={img.id} className="relative group aspect-video">
                      <img src={img.url?.startsWith("http") ? img.url : `${process.env.REACT_APP_BACKEND_URL}${img.url}`} alt={img.titre} className="w-full h-full object-cover rounded-lg" />
                      <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center">
                        <Button size="sm" variant="destructive" onClick={() => handleDeleteGalleryImage(img.id)}><Trash2 className="w-4 h-4" /></Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Avis */}
            {activeTab === "avis" && (
              <div className="bg-white rounded-xl border border-gray-200 p-8">
                <div className="flex justify-between items-center mb-6">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">Avis Clients ({reviews.length})</h2>
                    <p className="text-gray-500 text-sm">Les avis doivent être approuvés avant d'apparaître sur le site</p>
                  </div>
                  <Button onClick={() => setShowReviewDialog(true)} className="bg-yellow-500 hover:bg-yellow-600 text-white">
                    <Plus className="w-4 h-4 mr-2" /> Ajouter (Google)
                  </Button>
                </div>
                <div className="space-y-4">
                  {reviews.length === 0 ? (
                    <div className="text-center py-12 text-gray-500">Aucun avis pour le moment</div>
                  ) : reviews.map(review => (
                    <div key={review.id} className={`border rounded-lg p-6 ${review.approuve ? 'border-green-200 bg-green-50/50' : 'border-yellow-300 bg-yellow-50/50'}`}>
                      <div className="flex justify-between items-start">
                        <div>
                          <div className="flex items-center gap-3 mb-2">
                            <span className="font-semibold text-gray-900">{review.nom}</span>
                            <div className="flex">{renderStars(review.note)}</div>
                          </div>
                          {review.service && <p className="text-yellow-600 text-sm mb-2">{review.service}</p>}
                          <p className="text-gray-600 italic">"{review.commentaire}"</p>
                        </div>
                        <div className="flex gap-2">
                          {!review.approuve && (
                            <Button size="sm" variant="outline" className="text-green-600" onClick={() => handleApproveReview(review.id)}>
                              <Check className="w-4 h-4 mr-1" /> Approuver
                            </Button>
                          )}
                          <Button size="sm" variant="ghost" className="text-red-500" onClick={() => handleDeleteReview(review.id)}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Messages */}
            {activeTab === "messages" && (
              <div className="bg-white rounded-xl border border-gray-200 p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Messages ({contacts.length})</h2>
                <div className="space-y-4">
                  {contacts.map(contact => (
                    <div key={contact.id} className="border border-gray-200 rounded-lg p-6">
                      <div className="flex justify-between items-start mb-2">
                        <span className="font-semibold text-gray-900">{contact.nom}</span>
                        <span className="text-gray-500 text-sm">{contact.email}</span>
                      </div>
                      <p className="text-gray-600">{contact.message}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Paiements */}
            {activeTab === "paiements" && (
              <div className="bg-white rounded-xl border border-gray-200 p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Paiements ({payments.length})</h2>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Client</TableHead>
                        <TableHead>Montant</TableHead>
                        <TableHead>Statut</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {payments.map(payment => (
                        <TableRow key={payment.id}>
                          <TableCell>{payment.customer_email}</TableCell>
                          <TableCell>{(payment.amount / 100).toLocaleString()} €</TableCell>
                          <TableCell>
                            <span className={`px-2 py-1 rounded text-xs ${payment.payment_status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                              {payment.payment_status === 'paid' ? 'Payé' : 'En attente'}
                            </span>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}

            {/* Produits */}
            {activeTab === "produits" && (
              <div className="bg-white rounded-xl border border-gray-200 p-8">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold text-gray-900">Produits ({products.length})</h2>
                  <Button onClick={() => { setEditingProduct(null); setNewProduct({ nom: "", categorie: "calculateur-moteur", description: "", prix: 0, prix_promo: null, images: [], en_stock: true, reference: "" }); setShowProductDialog(true); }} className="bg-yellow-500 hover:bg-yellow-600 text-white">
                    <Plus className="w-4 h-4 mr-2" /> Ajouter
                  </Button>
                </div>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Photo</TableHead>
                        <TableHead>Produit</TableHead>
                        <TableHead>Catégorie</TableHead>
                        <TableHead>Prix</TableHead>
                        <TableHead>Stock</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {products.map(product => (
                        <TableRow key={product.id}>
                          <TableCell>
                            <img src={product.images?.[0]?.startsWith("http") ? product.images[0] : `${process.env.REACT_APP_BACKEND_URL}${product.images?.[0] || ""}`} alt="" className="w-16 h-16 object-cover rounded" onError={(e) => e.target.src = "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=100"} />
                          </TableCell>
                          <TableCell>
                            <div className="font-medium">{product.nom}</div>
                            {product.reference && <div className="text-xs text-gray-400">Réf: {product.reference}</div>}
                          </TableCell>
                          <TableCell className="text-sm text-gray-600">{product.categorie}</TableCell>
                          <TableCell>
                            <div className="font-semibold">{product.prix?.toLocaleString()} €</div>
                            {product.prix_promo && <div className="text-xs text-red-500">Promo: {product.prix_promo} €</div>}
                          </TableCell>
                          <TableCell>
                            <span className={`px-2 py-1 rounded text-xs ${product.en_stock ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                              {product.en_stock ? "En stock" : "Rupture"}
                            </span>
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-2">
                              <Button size="sm" variant="outline" onClick={() => handleEditProduct(product)}><Edit className="w-4 h-4" /></Button>
                              <Button size="sm" variant="destructive" onClick={() => handleDeleteProduct(product.id)}><Trash2 className="w-4 h-4" /></Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  {products.length === 0 && (
                    <div className="text-center py-12 text-gray-500">
                      Aucun produit. Cliquez sur "Ajouter" pour créer votre premier produit.
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Car Dialog */}
      <Dialog open={showCarDialog} onOpenChange={setShowCarDialog}>
        <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingCar ? "Modifier le véhicule" : "Ajouter un véhicule"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Marque</Label><Input value={newCar.marque} onChange={(e) => setNewCar({...newCar, marque: e.target.value})} className="mt-1" /></div>
              <div><Label>Modèle</Label><Input value={newCar.modele} onChange={(e) => setNewCar({...newCar, modele: e.target.value})} className="mt-1" /></div>
              <div><Label>Année</Label><Input type="number" value={newCar.annee} onChange={(e) => setNewCar({...newCar, annee: parseInt(e.target.value)})} className="mt-1" /></div>
              <div><Label>Prix (€)</Label><Input type="number" value={newCar.prix} onChange={(e) => setNewCar({...newCar, prix: parseInt(e.target.value)})} className="mt-1" /></div>
              <div><Label>Kilométrage</Label><Input type="number" value={newCar.kilometrage} onChange={(e) => setNewCar({...newCar, kilometrage: parseInt(e.target.value)})} className="mt-1" /></div>
              <div><Label>Puissance</Label><Input value={newCar.puissance} onChange={(e) => setNewCar({...newCar, puissance: e.target.value})} className="mt-1" placeholder="150 ch" /></div>
              <div>
                <Label>Carburant</Label>
                <Select value={newCar.carburant} onValueChange={(v) => setNewCar({...newCar, carburant: v})}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Essence">Essence</SelectItem>
                    <SelectItem value="Diesel">Diesel</SelectItem>
                    <SelectItem value="Hybride">Hybride</SelectItem>
                    <SelectItem value="Électrique">Électrique</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Transmission</Label>
                <Select value={newCar.transmission} onValueChange={(v) => setNewCar({...newCar, transmission: v})}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Automatique">Automatique</SelectItem>
                    <SelectItem value="Manuelle">Manuelle</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2"><Label>Couleur</Label><Input value={newCar.couleur} onChange={(e) => setNewCar({...newCar, couleur: e.target.value})} className="mt-1" /></div>
              <div className="col-span-2">
                <MultiImageUploader label="Photos du véhicule" values={newCar.images || []} onChange={(urls) => setNewCar({...newCar, images: urls})} maxImages={20} />
              </div>
              <div className="col-span-2"><Label>Description</Label><Textarea value={newCar.description} onChange={(e) => setNewCar({...newCar, description: e.target.value})} className="mt-1" /></div>
            </div>
          </div>
          <Button onClick={handleSaveCar} className="w-full bg-yellow-500 hover:bg-yellow-600 text-white">{editingCar ? "Modifier" : "Ajouter"}</Button>
        </DialogContent>
      </Dialog>

      {/* Gallery Dialog */}
      <Dialog open={showGalleryDialog} onOpenChange={setShowGalleryDialog}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle>Ajouter une image</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <ImageUploader label="Image" value={newGalleryImage.url} onChange={(url) => setNewGalleryImage({...newGalleryImage, url})} />
            <div><Label>Titre</Label><Input value={newGalleryImage.titre} onChange={(e) => setNewGalleryImage({...newGalleryImage, titre: e.target.value})} className="mt-1" /></div>
            <div><Label>Description</Label><Input value={newGalleryImage.description} onChange={(e) => setNewGalleryImage({...newGalleryImage, description: e.target.value})} className="mt-1" /></div>
          </div>
          <Button onClick={handleAddGalleryImage} className="w-full bg-yellow-500 hover:bg-yellow-600 text-white">Ajouter</Button>
        </DialogContent>
      </Dialog>

      {/* Review Dialog */}
      <Dialog open={showReviewDialog} onOpenChange={setShowReviewDialog}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle>Ajouter un avis (Google)</DialogTitle>
            <DialogDescription>Copiez un avis Google pour l'afficher sur votre site</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div><Label>Nom du client</Label><Input value={newReview.nom} onChange={(e) => setNewReview({...newReview, nom: e.target.value})} className="mt-1" /></div>
            <div>
              <Label>Note</Label>
              <div className="flex gap-2 mt-2">
                {[1,2,3,4,5].map(n => (
                  <button key={n} type="button" onClick={() => setNewReview({...newReview, note: n})}>
                    <Star className={`w-8 h-8 ${n <= newReview.note ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`} />
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label>Service</Label>
              <Select value={newReview.service} onValueChange={(v) => setNewReview({...newReview, service: v})}>
                <SelectTrigger className="mt-1"><SelectValue placeholder="Optionnel" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Réparation mécanique">Réparation mécanique</SelectItem>
                  <SelectItem value="Achat véhicule">Achat véhicule</SelectItem>
                  <SelectItem value="Serrurerie automobile">Serrurerie automobile</SelectItem>
                  <SelectItem value="Électronique auto">Électronique auto</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Commentaire</Label><Textarea value={newReview.commentaire} onChange={(e) => setNewReview({...newReview, commentaire: e.target.value})} className="mt-1" placeholder="Copiez le texte de l'avis..." /></div>
          </div>
          <Button onClick={handleAddReview} className="w-full bg-yellow-500 hover:bg-yellow-600 text-white">Ajouter et publier</Button>
        </DialogContent>
      </Dialog>

      {/* Product Dialog */}
      <Dialog open={showProductDialog} onOpenChange={setShowProductDialog}>
        <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingProduct ? "Modifier le produit" : "Ajouter un produit"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2"><Label>Nom du produit</Label><Input value={newProduct.nom} onChange={(e) => setNewProduct({...newProduct, nom: e.target.value})} className="mt-1" placeholder="Ex: Calculateur moteur Bosch EDC17" /></div>
              <div>
                <Label>Catégorie</Label>
                <Select value={newProduct.categorie} onValueChange={(v) => setNewProduct({...newProduct, categorie: v})}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="calculateur-moteur">Calculateur moteur</SelectItem>
                    <SelectItem value="boitier-habitacle">Boitier habitacle</SelectItem>
                    <SelectItem value="boitier-fusible">Boitier fusible</SelectItem>
                    <SelectItem value="calculateur-airbag">Calculateur airbag</SelectItem>
                    <SelectItem value="bloc-abs">Bloc ABS</SelectItem>
                    <SelectItem value="serrurerie">Serrurerie</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Référence</Label><Input value={newProduct.reference} onChange={(e) => setNewProduct({...newProduct, reference: e.target.value})} className="mt-1" placeholder="Ex: EDC17C46" /></div>
              <div><Label>Prix (€)</Label><Input type="number" value={newProduct.prix} onChange={(e) => setNewProduct({...newProduct, prix: parseFloat(e.target.value) || 0})} className="mt-1" /></div>
              <div><Label>Prix promo (€) - optionnel</Label><Input type="number" value={newProduct.prix_promo || ""} onChange={(e) => setNewProduct({...newProduct, prix_promo: e.target.value ? parseFloat(e.target.value) : null})} className="mt-1" placeholder="Laisser vide si pas de promo" /></div>
              <div className="col-span-2"><Label>Description</Label><Textarea value={newProduct.description} onChange={(e) => setNewProduct({...newProduct, description: e.target.value})} className="mt-1" placeholder="Description du produit..." /></div>
              <div className="col-span-2">
                <MultiImageUploader label="Photos du produit (jusqu'à 20)" values={newProduct.images || []} onChange={(urls) => setNewProduct({...newProduct, images: urls})} maxImages={20} />
              </div>
              <div className="col-span-2 flex items-center gap-2">
                <input type="checkbox" id="en_stock" checked={newProduct.en_stock} onChange={(e) => setNewProduct({...newProduct, en_stock: e.target.checked})} className="w-4 h-4" />
                <Label htmlFor="en_stock">En stock</Label>
              </div>
            </div>
          </div>
          <Button onClick={handleSaveProduct} className="w-full bg-yellow-500 hover:bg-yellow-600 text-white">{editingProduct ? "Modifier" : "Ajouter"}</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
